/**
 * AssistantContext — shared open/close state, conversation history,
 * and the Cmd+K / Ctrl+K global shortcut.
 *
 * Mounted once high in the tree so both the launcher pill and the
 * palette read the same state, and so the shortcut works from any
 * page without the user clicking into the palette first.
 *
 * History is kept in component memory (lost on full page reload) —
 * that's intentional. Persisting Q&A across sessions on the client
 * means we'd need to be very careful about not leaking another user's
 * questions if two people share a browser. Easier to just start fresh.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { ToolCall } from '../../api/wabot';

export interface AssistantTurn {
  id: string;
  question: string;
  answer: string;
  tool_calls: ToolCall[];
  latency_ms: number;
  asked_at: number; // epoch ms
  error?: string;
}

interface AssistantState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;

  history: AssistantTurn[];
  addTurn: (turn: AssistantTurn) => void;
  clearHistory: () => void;
}

const AssistantContext = createContext<AssistantState | undefined>(undefined);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<AssistantTurn[]>([]);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  const addTurn = useCallback((turn: AssistantTurn) => {
    setHistory((h) => [...h, turn].slice(-20)); // keep last 20
  }, []);

  const clearHistory = useCallback(() => setHistory([]), []);

  // Global keyboard: Cmd/Ctrl+K toggles, Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const value = useMemo<AssistantState>(
    () => ({ open, setOpen, toggle, history, addTurn, clearHistory }),
    [open, toggle, history, addTurn, clearHistory],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantState {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used inside <AssistantProvider>');
  return ctx;
}
